// src/pages/LoginPage.jsx
import React, { useState, useEffect } from "react";
import axios from "../axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getGuestScans, clearGuestScans } from "../utils/guestStorage";
import { trackAction } from "../services/sessionLogger";
import { getMessaging, getToken } from "firebase/messaging";
import { messaging } from "../firebase-messaging";
import "../styles/login.css";
import BackButton from "../components/BackButton";


// Enregistre le token FCM après connexion — silencieux si permission refusée
const ensureFcmToken = async (userId) => {
  try {
    if (typeof Notification === "undefined" || !messaging) return;
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    const vapidKey =
      process.env.REACT_APP_PUBLIC_VAPID_KEY ||
      "BBGKXZTNZYudlq-1Byuovg-i0ikbgmZBd12ZeIYMsHA8I0lbIfOo7UrVjUInsgkdnKdBwXzcjie8ke1-Rp8KICY";

    const registration = await navigator.serviceWorker.ready;

    const fcmToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (!fcmToken) return;

    localStorage.setItem("fcm_token", fcmToken);

    await axios.post("/push/save-fcm-token", { userId, fcmToken });
  } catch (err) {
    console.warn("⚠️ [FCM] Token non enregistré (silencieux) :", err.message);
  }
};

const LoginPage = () => {
  const [loginMode, setLoginMode] = useState("email"); // "email" | "pseudo"
  const [formData, setFormData] = useState({ email: "", username: "", password: "" });
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("welcome") === "1") {
      toast.success("🎉 Votre compte est activé ! Bienvenue sur CrushNow 💘");
    }
    if (params.get("verified") === "1") {
      toast.success("✅ Compte confirmé ! Connecte-toi pour commencer ta chasse aux bons plans 🛍️");
    }
    if (params.get("reset") === "1") {
      toast.success("✅ Mot de passe modifié ! Connectez-vous.");
    }
  }, [location.search]);
  const barcodeParam = new URLSearchParams(location.search).get("barcode");

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const switchMode = (mode) => {
    setLoginMode(mode);
    setFormData({ email: "", username: "", password: "" });
  };

  const syncGuestScans = async (userId) => {
    const guestScans = getGuestScans();
    if (!guestScans.length) return;
    try {
      const enrichedScans = guestScans.map((scan) => ({
        userId,
        id: scan.id,
        name: scan.name,
      }));

      await axios.post("/scans/batch", { scans: enrichedScans });
      clearGuestScans();
      toast.success(`✅ ${enrichedScans.length} scan(s) importé(s)`);
    } catch {
      toast.error("❌ Erreur lors de l'import invité");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackAction("Se connecter");
    console.log("🔐 [Login] Début handleSubmit, mode :", loginMode);
    try {
      const payload = loginMode === "email"
        ? { email: formData.email, password: formData.password }
        : { username: formData.username, password: formData.password };

      const res = await axios.post("/auth/login", payload);
      console.log("✅ [Login] Réponse API reçue :", res.status);
      const { user, token } = res.data;

      if (user && token) {
        const userData = { ...user, token };
        localStorage.setItem("user", JSON.stringify(userData));
        console.log("💾 [Login] localStorage écrit, rôle :", user.role);

        ensureFcmToken(user._id || user.id).catch((err) =>
          console.warn("⚠️ [FCM] Erreur non bloquante :", err)
        );

        toast.success("Connexion réussie !");

        if (user.role === "client") {
          try { await syncGuestScans(user._id || user.id); } catch (e) { console.warn("⚠️ [Login] syncGuestScans non bloquant :", e); }
          console.log("🧭 [Login] Navigation vers /client");
          navigate("/client");
        } else {
          const dest = barcodeParam ? `/dashboard?barcode=${barcodeParam}` : "/dashboard";
          console.log("🧭 [Login] Navigation vers", dest);
          navigate(dest);
        }
      } else {
        toast.error("❌ Réponse serveur incomplète");
      }
    } catch (err) {
      console.error("❌ [Login] Erreur handleSubmit :", err);
      if (err.response?.status === 429) {
        toast.error("Trop de tentatives de connexion. Réessaie dans 15 minutes.");
        return;
      }
      if (err.response?.data?.error === "email_not_verified") {
        setUnverifiedEmail(err.response.data.email);
        return;
      }
      toast.error(err.response?.data?.message || "Échec de la connexion");
    }
  };

  // ── Toggle mode ────────────────────────────────────────────────────────────
  const toggleStyle = (active) => ({
    flex: 1,
    padding: "8px",
    border: "1.5px solid",
    borderColor: active ? "#D41A6E" : "#CBD5E1",
    borderRadius: "8px",
    background: active ? "#D41A6E" : "white",
    color: active ? "white" : "#64748b",
    fontWeight: active ? 700 : 400,
    fontSize: "14px",
    cursor: "pointer",
  });

  return (
    <div className="login-wrapper">
      <ToastContainer theme="colored" />

      <form onSubmit={handleSubmit} className="login-card">
        <BackButton />
        <h2 className="login-title">Connexion</h2>

        {unverifiedEmail && (
          <div style={{
            background: "#fefce8", border: "1px solid #fde047",
            borderRadius: "8px", padding: "12px 16px", marginBottom: "12px",
            fontSize: "13px", color: "#854d0e"
          }}>
            ⚠️ Email non confirmé.{" "}
            <button
              onClick={async () => {
                await axios.post("/auth/resend-verification", { email: unverifiedEmail });
                toast.success("📧 Email renvoyé !");
                setUnverifiedEmail(null);
              }}
              style={{ background: "none", border: "none", color: "#e11d48",
                       fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
            >
              Renvoyer le lien
            </button>
          </div>
        )}

        {/* ── Toggle mode ── */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
          <button type="button" style={toggleStyle(loginMode === "email")} onClick={() => switchMode("email")}>
            Avec email
          </button>
          <button type="button" style={toggleStyle(loginMode === "pseudo")} onClick={() => switchMode("pseudo")}>
            Sans email
          </button>
        </div>

        {loginMode === "email" ? (
          <input
            name="email"
            type="email"
            placeholder="Email"
            onChange={handleChange}
            onFocus={(e) => { setTimeout(() => { e.target.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300); }}
            className="login-input"
          />
        ) : (
          <input
            name="username"
            type="text"
            placeholder="Pseudonyme"
            onChange={handleChange}
            onFocus={(e) => { setTimeout(() => { e.target.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300); }}
            className="login-input"
          />
        )}

        <div style={{ position: "relative" }}>
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Mot de passe"
            onChange={handleChange}
            onFocus={(e) => { setTimeout(() => { e.target.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300); }}
            className="login-input"
            style={{ paddingRight: "40px", width: "100%" }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute", right: "10px", top: "50%",
              transform: "translateY(-50%)", background: "none",
              border: "none", cursor: "pointer", color: "#94a3b8",
              fontSize: "18px", padding: "0"
            }}
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>

        <button
          type="submit"
          className="login-btn"
          style={{ touchAction: "manipulation" }}
        >
          Se connecter
        </button>

        <p style={{ textAlign: "right", fontSize: "12px", marginTop: "4px" }}>
          <span
            onClick={() => navigate("/recover-account")}
            style={{ color: "#e11d48", cursor: "pointer" }}
          >
            Récupérer mon compte
          </span>
        </p>

        <p className="login-text">
          Pas encore de compte ?{" "}
          <Link to="/register" className="login-link">
            Créer un compte
          </Link>
        </p>

        <Link to="/" className="login-back">
          Retour à l'accueil
        </Link>

        <footer className="login-footer">
          <p>
            <Link to="/evolutions">Évolutions</Link> |{" "}
            <Link to="/faq">FAQ</Link> |{" "}
            <Link to="/cgu">CGU</Link> |{" "}
            <Link to="/privacy">Politique de confidentialité</Link> |{" "}
            <Link to="/mentions">Mentions légales</Link> |{" "}
            <Link to="/about">À propos</Link>
          </p>

          <p className="brand">
            💘 <strong>CrushNow®</strong> — © 2025 Tous droits réservés.
          </p>
        </footer>
      </form>
    </div>
  );
};

export default LoginPage;
